import React from 'react'
import { RotateCcw } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * AgentConsole (W10) — the flex of the AI-native section, dark. A chat/console
 * panel that plays the copy doc's §W10 script ONCE when scrolled into view:
 *   1. the operator message appears (right-aligned),
 *   2. the agent's reply streams in, line by line, with a typing cursor,
 *   3. an "Approve & apply" affordance resolves to the applied state line.
 *
 * Every visible string is quoted VERBATIM from the copy doc's §W10 (AgentConsole);
 * the role="img" label is the copy doc's §Widget aria-labels W10 line. The three
 * agent-reply lines carry the LOCKED Target vocabulary deliberately ("On track" /
 * "Pushing" canonical state labels, "ADR floor" exact) — do NOT paraphrase.
 *
 * No cadence, no PMS name, no stage counts (§0 do-not-publish list). Names in the
 * MCP rail (rendered by the sibling McpRail widget) are text only — no logos.
 *
 * Reduced motion (or no matchMedia / no IntersectionObserver): the full
 * conversation + applied state, static — same pattern as LedgerConsole3.
 */

const CONSOLE_LABEL = 'Revenue agent'
const OPERATOR_MESSAGE = 'August looks soft after the 15th — plan a response.'

// Agent reply — three lines, streamed in order. `hot` fragments render as the
// glowing locked-vocabulary state labels; they stay part of the verbatim string.
interface ReplyLine {
  /** The verbatim line text, split around any state-label emphasis */
  before: string
  hot?: string
  after?: string
}
const AGENT_REPLY: ReplyLine[] = [
  { before: 'Ease weekday premiums across 16–31 August to lift bookings.' },
  { before: 'Your ADR floor stays intact — no rate drops below it.' },
  { before: 'Target check: this keeps August ', hot: 'On track', after: ', not Pushing.' },
]

const APPROVE_LABEL = 'Approve & apply'
const APPLIED_LINE = 'Applied — inside your guardrails.'

// Timeline (ms) — operator bubble in, a beat, then each reply line.
const OPERATOR_DELAY_MS = 500
const FIRST_REPLY_DELAY_MS = 1150
const REPLY_INTERVAL_MS = 900

function prefersReducedMotion(): boolean {
  return (
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches
  )
}

type Phase = 'idle' | 'operator' | 'replying' | 'awaiting' | 'applied'

export default function AgentConsole({ className }: { className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [reduced] = React.useState(prefersReducedMotion)
  const [runId, setRunId] = React.useState(0)

  // Progress markers. `linesShown` counts revealed reply lines; `phase` gates the
  // operator bubble, the streaming, the Approve affordance and the applied state.
  const [phase, setPhase] = React.useState<Phase>(reduced ? 'applied' : 'idle')
  const [linesShown, setLinesShown] = React.useState(reduced ? AGENT_REPLY.length : 0)
  const [started, setStarted] = React.useState(false)

  // Start the script once the console scrolls into view (play-once).
  React.useEffect(() => {
    if (reduced) return
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setStarted(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setStarted(true)
          io.disconnect()
        }
      },
      { threshold: 0.35 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [reduced])

  // The scripted timeline. Re-runs on replay (runId) from a clean slate.
  React.useEffect(() => {
    if (reduced || !started) return
    setPhase('idle')
    setLinesShown(0)
    const timers: ReturnType<typeof setTimeout>[] = []

    timers.push(setTimeout(() => setPhase('operator'), OPERATOR_DELAY_MS))
    timers.push(setTimeout(() => setPhase('replying'), FIRST_REPLY_DELAY_MS - 120))

    for (let i = 1; i <= AGENT_REPLY.length; i++) {
      timers.push(
        setTimeout(() => setLinesShown(i), FIRST_REPLY_DELAY_MS + (i - 1) * REPLY_INTERVAL_MS)
      )
    }
    // Once the last line has landed, expose the Approve affordance.
    timers.push(
      setTimeout(
        () => setPhase('awaiting'),
        FIRST_REPLY_DELAY_MS + AGENT_REPLY.length * REPLY_INTERVAL_MS
      )
    )

    return () => timers.forEach(clearTimeout)
  }, [started, reduced, runId])

  const animate = !reduced
  const operatorVisible = reduced || phase === 'operator' || phase === 'replying' || phase === 'awaiting' || phase === 'applied'
  const streaming = phase === 'replying' && linesShown < AGENT_REPLY.length
  const awaiting = phase === 'awaiting'
  const applied = reduced || phase === 'applied'
  const canReplay = animate && applied

  return (
    <div
      ref={ref}
      className={cn('mkt3-panel mkt3-panel-hot overflow-hidden', className)}
      role="img"
      aria-label="A revenue agent console — the operator asks for a response to a soft August, and the agent replies with a three-line plan that eases weekday premiums, keeps the ADR floor intact, and keeps the month on track, ready to approve and apply."
    >
      {/* Console header */}
      <div className="flex items-center justify-between gap-3 border-b border-white/[0.07] px-4 py-3 sm:px-5">
        <div className="flex items-center gap-2.5">
          <span
            aria-hidden="true"
            className="mkt3-live-dot h-1.5 w-1.5 rounded-full bg-teal-400 shadow-[0_0_8px_rgba(45,212,191,0.9)]"
          />
          <span className="font-mono text-[10px] font-semibold uppercase tracking-[0.22em] text-teal-300">
            {CONSOLE_LABEL}
          </span>
        </div>
        {canReplay && (
          <button
            type="button"
            onClick={() => setRunId((r) => r + 1)}
            aria-label="Replay"
            className="inline-flex items-center gap-1.5 rounded-md px-1.5 py-1 text-slate-500 transition-all duration-300 hover:bg-white/10 hover:text-teal-300"
          >
            <RotateCcw className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="font-mono text-[10px] uppercase tracking-[0.16em]">Replay</span>
          </button>
        )}
      </div>

      {/* Conversation transcript */}
      <div className="space-y-4 px-4 py-5 sm:px-5" aria-hidden="true">
        {/* Operator message — right-aligned */}
        <div
          className={cn(
            'flex justify-end transition-all duration-500',
            operatorVisible ? 'translate-y-0 opacity-100' : 'translate-y-1.5 opacity-0'
          )}
        >
          <p className="max-w-[85%] rounded-2xl rounded-br-md bg-teal-500/[0.14] px-3.5 py-2.5 text-[13px] leading-snug text-teal-50 ring-1 ring-inset ring-teal-400/25 sm:text-sm">
            {OPERATOR_MESSAGE}
          </p>
        </div>

        {/* Agent reply — streams in line by line, left-aligned */}
        <div className="flex justify-start">
          <div className="max-w-[92%] rounded-2xl rounded-bl-md bg-white/[0.04] px-3.5 py-3 ring-1 ring-inset ring-white/[0.08]">
            <p className="mb-2 font-mono text-[9px] font-semibold uppercase tracking-[0.2em] text-teal-300/80">
              {CONSOLE_LABEL}
            </p>
            <ol className="space-y-2">
              {AGENT_REPLY.map((line, i) => {
                const shown = linesShown > i
                const isLastShown = streaming && linesShown === i + 1
                return (
                  <li
                    key={i}
                    className={cn(
                      'mkt3-console-row flex gap-2.5 text-[13px] leading-snug text-slate-200 sm:text-sm',
                      shown && 'mkt3-on'
                    )}
                  >
                    <span
                      aria-hidden="true"
                      className="mt-[0.45em] h-1 w-1 shrink-0 rounded-full bg-teal-400/70"
                    />
                    <span className="min-w-0">
                      {line.before}
                      {line.hot && <span className="font-semibold text-teal-300">{line.hot}</span>}
                      {line.after}
                      {animate && isLastShown && (
                        <span className="mkt3-cursor ml-0.5 inline-block h-[1em] w-[2px] translate-y-[0.12em] bg-teal-300/80" />
                      )}
                    </span>
                  </li>
                )
              })}
            </ol>

            {/* A typing indicator before the very first line lands */}
            {animate && phase === 'replying' && linesShown === 0 && (
              <div className="flex items-center gap-2 pt-1">
                <span className="font-mono text-[11px] text-slate-500">thinking</span>
                <span className="mkt3-cursor inline-block h-[1em] w-[2px] translate-y-[0.1em] bg-teal-300/70" />
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Approve affordance → applied state */}
      <div className="border-t border-white/[0.07] px-4 py-4 sm:px-5">
        {applied ? (
          <div className="flex items-center gap-2.5">
            <span
              aria-hidden="true"
              className="mkt3-pop inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-teal-500/20 text-teal-300 ring-1 ring-inset ring-teal-400/40"
            >
              <svg viewBox="0 0 12 12" className="h-3 w-3" fill="none" stroke="currentColor" strokeWidth="1.8" aria-hidden="true">
                <path d="M2.5 6.2 5 8.6 9.6 3.6" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
            <span className="font-mono text-[11px] uppercase tracking-[0.14em] text-teal-300/90">
              {APPLIED_LINE}
            </span>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setPhase('applied')}
            disabled={!awaiting}
            className={cn(
              'mkt3-cta-dark inline-flex h-9 items-center rounded-lg bg-teal-500 px-4 text-sm font-semibold text-slate-950 transition-all duration-300',
              awaiting ? 'opacity-100' : 'pointer-events-none opacity-35'
            )}
          >
            {APPROVE_LABEL}
          </button>
        )}
      </div>
    </div>
  )
}
